import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Camera, Palette, Shield, Moon, Sun, Upload, Check, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFILE_IMAGES = [
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Oliver',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Max',
  'https://api.dicebear.com/7.x/avataaars/svg?seed=Luna'
];

const BANNER_COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b',
  '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'
];

const ProfileSettings = () => {
  const { user, token, updateUser } = useAuth();
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedBanner, setSelectedBanner] = useState('#3b82f6');
  const [selectedTheme, setSelectedTheme] = useState('light');
  const [selectedPrivacy, setSelectedPrivacy] = useState('public');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const fileInputRef = useRef(null);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (user) {
      setSelectedImage(user.profile_image || PROFILE_IMAGES[0]);
      setSelectedBanner(user.banner_color || '#3b82f6');
      setSelectedTheme(user.theme || 'light');
      setSelectedPrivacy(user.privacy || 'public');
      setBio(user.bio || '');
    }
  }, [user]);

  // Auto-save function
  const saveProfile = useCallback(async (data) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/profile`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastSaved(new Date());
      
      // Update user context immediately
      updateUser(data);
      
      // Apply theme immediately
      if (data.theme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  }, [token, updateUser]);

  // Debounced auto-save
  const autoSave = useCallback((updates) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveProfile({
        profile_image: updates.profile_image ?? selectedImage,
        banner_color: updates.banner_color ?? selectedBanner,
        theme: updates.theme ?? selectedTheme,
        privacy: updates.privacy ?? selectedPrivacy,
        bio: updates.bio ?? bio
      });
    }, 500);
  }, [saveProfile, selectedImage, selectedBanner, selectedTheme, selectedPrivacy, bio]);

  // Handle avatar selection
  const handleAvatarSelect = (img) => {
    setSelectedImage(img);
    autoSave({ profile_image: img });
  };

  // Handle banner color selection
  const handleBannerSelect = (color) => {
    setSelectedBanner(color);
    autoSave({ banner_color: color });
  };

  // Handle theme change
  const handleThemeChange = (theme) => {
    setSelectedTheme(theme);
    autoSave({ theme });
  };

  // Handle privacy change
  const handlePrivacyChange = (privacy) => {
    setSelectedPrivacy(privacy);
    autoSave({ privacy });
  };

  // Handle bio change with longer debounce
  const handleBioChange = (e) => {
    const newBio = e.target.value;
    setBio(newBio);
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveProfile({
        profile_image: selectedImage,
        banner_color: selectedBanner,
        theme: selectedTheme,
        privacy: selectedPrivacy,
        bio: newBio
      });
    }, 1000);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please upload an image file',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 5MB',
        variant: 'destructive'
      });
      return;
    }

    // Convert to base64 data URL
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result;
      if (dataUrl) {
        setSelectedImage(dataUrl);
        autoSave({ profile_image: dataUrl });
        toast({
          title: 'Image uploaded!',
          description: 'Your profile image has been updated.'
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="profile-settings">
      {/* Auto-save indicator */}
      <div className="flex items-center justify-end text-sm text-gray-500">
        {saving ? (
          <span className="flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving...
          </span>
        ) : lastSaved ? (
          <span className="flex items-center gap-1 text-green-600">
            <Check className="h-3 w-3" />
            Saved
          </span>
        ) : null}
      </div>

      {/* Banner Preview */}
      <div 
        className="w-full h-32 rounded-xl transition-colors duration-300"
        style={{ backgroundColor: selectedBanner }}
        data-testid="banner-preview"
      />

      {/* Profile Preview */}
      <div className="flex justify-center -mt-16 mb-4">
        <div className="relative group">
          <img
            src={selectedImage}
            alt="Profile"
            className="w-24 h-24 rounded-full border-4 border-white shadow-lg bg-white object-cover"
            data-testid="profile-image-preview"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            data-testid="upload-image-btn"
          >
            <Upload className="h-6 w-6 text-white" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            data-testid="image-file-input"
          />
        </div>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-xl font-bold text-gray-900">{user?.username}</h2>
        <p className="text-sm text-gray-600">{user?.email}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Profile Image
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-6 gap-3">
            {PROFILE_IMAGES.map((img, idx) => (
              <div
                key={idx}
                onClick={() => handleAvatarSelect(img)}
                className={`cursor-pointer rounded-full border-2 p-1 transition-all ${
                  selectedImage === img
                    ? 'border-blue-500 scale-110'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                data-testid={`avatar-option-${idx}`}
              >
                <img src={img} alt="Avatar" className="w-full rounded-full bg-white" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="flex-1"
              data-testid="upload-btn"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Your Own
            </Button>
          </div>
          <p className="text-xs text-gray-500 text-center">
            Click on an avatar or upload your own image (max 5MB)
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Banner Color
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-8 gap-3">
            {BANNER_COLORS.map((color) => (
              <div
                key={color}
                onClick={() => handleBannerSelect(color)}
                className={`cursor-pointer rounded-lg h-10 border-2 transition-all ${
                  selectedBanner === color
                    ? 'border-gray-900 scale-110'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
                style={{ backgroundColor: color }}
                data-testid={`banner-color-${color.replace('#', '')}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bio</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Tell others about yourself..."
            value={bio}
            onChange={handleBioChange}
            rows={3}
            maxLength={200}
            data-testid="bio-textarea"
          />
          <p className="text-xs text-gray-500 mt-1">{bio.length}/200</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Privacy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedPrivacy} onValueChange={handlePrivacyChange}>
            <SelectTrigger data-testid="privacy-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public - Everyone can see</SelectItem>
              <SelectItem value="friends">Friends Only</SelectItem>
              <SelectItem value="private">Private - Only me</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {selectedTheme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            Theme
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button
              type="button"
              variant={selectedTheme === 'light' ? 'default' : 'outline'}
              onClick={() => handleThemeChange('light')}
              className="flex-1"
              data-testid="theme-light-btn"
            >
              <Sun className="mr-2 h-4 w-4" />
              Light
            </Button>
            <Button
              type="button"
              variant={selectedTheme === 'dark' ? 'default' : 'outline'}
              onClick={() => handleThemeChange('dark')}
              className="flex-1"
              data-testid="theme-dark-btn"
            >
              <Moon className="mr-2 h-4 w-4" />
              Dark
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileSettings;
